import {
  DirectiveLocation,
  GraphQLField,
  GraphQLObjectType,
  GraphQLSchema,
  GraphQLType,
  SchemaMetaFieldDef,
  TypeMetaFieldDef,
  TypeNameMetaFieldDef,
  isAbstractType,
  isListType,
  isNonNullType,
  isObjectType,
} from 'graphql';
import {
  Position as GraphQLPosition,
  State,
  getAutocompleteSuggestions,
} from 'graphql-language-service';
import { getContextAtPosition } from 'graphql-language-service/dist/parser';
import { Maybe } from 'graphql/jsutils/Maybe';
import { TypeMap } from 'graphql/type/schema';
import * as monaco from 'monaco-editor';
import { CompletionItem } from 'vscode-languageserver';

import { getCurrentDocsContext } from 'src/app/graph/explorerPage/explorerDocs/getFieldToggle/getCurrentDocsContext';
import {
  getArgument,
  getArgumentDefinition,
  getVariableDefinitionEdits,
} from 'src/app/graph/explorerPage/explorerDocs/getFieldToggle/getInsertion';
import { getPathToOffset } from 'src/app/graph/explorerPage/helpers/graphqlParseHelpers';

const DirectiveLocations = Object.keys(DirectiveLocation);

function hasFields(type: GraphQLType): boolean {
  return (
    isObjectType(type) ||
    (isListType(type) && hasFields(type.ofType)) ||
    (isNonNullType(type) && hasFields(type.ofType))
  );
}

function inferCompletionKind({
  state,
  suggestion,
  typeMap,
}: {
  state: Maybe<State>;
  suggestion: CompletionItem;
  typeMap: TypeMap;
}): monaco.languages.CompletionItemKind {
  switch (state?.kind) {
    case 'Document':
      return monaco.languages.CompletionItemKind.Keyword;
    case 'Field':
    case 'AliasedField':
    case 'SelectionSet': {
      // "[SomeTypeName!]!" -> "SomeTypeName"
      const fieldTypeName = suggestion?.detail?.replace(/[\[\]\!]/g, '');
      const fieldType = fieldTypeName && typeMap[fieldTypeName];

      if (fieldType instanceof GraphQLObjectType) {
        return monaco.languages.CompletionItemKind.Module;
      }

      return monaco.languages.CompletionItemKind.Field;
    }
    case 'Arguments':
      return monaco.languages.CompletionItemKind.Variable;
    case 'FragmentSpread':
    case 'TypeCondition':
    case 'NamedType':
      return monaco.languages.CompletionItemKind.Interface;
    default:
      return monaco.languages.CompletionItemKind.Text;
  }
}

/**
 * Factory function to create `CompletionItemProvider`.
 */
export function schemaCompletionProviderFactory({
  schemaRef,
  suggestionAcceptedCommandID,
  shouldManageVariablesRef,
}: {
  schemaRef: React.RefObject<GraphQLSchema | undefined>;
  suggestionAcceptedCommandID: string | null;
  shouldManageVariablesRef: React.MutableRefObject<boolean>;
}): monaco.languages.CompletionItemProvider {
  return {
    async provideCompletionItems(model, monacoPosition) {
      const schemaRefCurrent = schemaRef.current;
      if (!schemaRefCurrent) {
        return { suggestions: [] };
      }

      const typeMap = schemaRefCurrent.getTypeMap();

      const word = model.getWordUntilPosition(monacoPosition);
      const range = {
        startLineNumber: monacoPosition.lineNumber,
        endLineNumber: monacoPosition.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      // Monaco records positions differently than our tooling does.
      const apolloPosition = new GraphQLPosition(
        monacoPosition.lineNumber - 1,
        monacoPosition.column - 1,
      );

      const currentDocsContext = getCurrentDocsContext(
        model,
        new monaco.Position(range.startLineNumber, range.startColumn),
        [],
        schemaRefCurrent,
      );

      const source = model.getValue();
      const context = getContextAtPosition(
        source,
        apolloPosition,
        schemaRefCurrent,
      );
      if (!context) {
        return { suggestions: [] };
      }
      const { token, state, typeInfo } = context;

      if (state.kind === ('DirectiveLocation' as unknown)) {
        return {
          suggestions: DirectiveLocations.map((location) => ({
            label: location,
            kind: monaco.languages.CompletionItemKind.Constant,
            documentation: '',
            insertText: location,
            insertTextRules:
              monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
          })),
        };
      } else if (
        (state.kind === 'FragmentSpread' &&
          token.string?.trim().length === 0) ||
        token.string === '...'
      ) {
        return {
          suggestions: [
            {
              label: '@defer',
              kind: monaco.languages.CompletionItemKind.Snippet,
              documentation: '',
              insertText: `${
                token.string === '...' ? ' ' : ''
              }@defer {\n\t$0\n}`,
              insertTextRules:
                monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              range,
              command: { id: 'editor.action.triggerSuggest', title: '' },
            },
          ],
        };
      }

      const suggestions = getAutocompleteSuggestions(
        schemaRefCurrent,
        source,
        apolloPosition,
      ).map((suggestion) => ({
        label: suggestion.label,
        kind: inferCompletionKind({ state, suggestion, typeMap }),
        detail: suggestion.detail,
        // Monaco does not support custom sorting for items (or plan to,
        // https://github.com/Microsoft/monaco-editor/issues/1077).
        // However there is an option to provide text to sort by. We want
        // to show '__' suggestions at the end of the list instead of at
        // the front, so add a lexical prefix.
        sortText: `${suggestion.label.startsWith('__') ? 'z' : 'a'}${
          suggestion.label
        }`,
        documentation: suggestion.documentation,
        insertText: suggestion.insertText || suggestion.label,
        insertTextRules:
          monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        range,
      }));

      if (
        state.kind === 'SelectionSet' ||
        state.kind === 'Field' ||
        state.kind === 'AliasedField'
      ) {
        const parentType: Maybe<
          GraphQLType & {
            clientSchema?: { localFields?: unknown };
          }
        > = typeInfo.parentType;

        if (!parentType || !('getFields' in parentType)) {
          return {
            suggestions: [],
          };
        }

        const parentFields = {
          ...(parentType.getFields() as {
            [label: string]: GraphQLField<unknown, unknown>;
          }),
        };

        if (isAbstractType(parentType)) {
          parentFields[TypeNameMetaFieldDef.name] = TypeNameMetaFieldDef;
        }

        if (parentType === schemaRefCurrent.getQueryType()) {
          parentFields[SchemaMetaFieldDef.name] = SchemaMetaFieldDef;
          parentFields[TypeMetaFieldDef.name] = TypeMetaFieldDef;
        }

        return {
          suggestions: suggestions.map(
            (suggestion): monaco.languages.CompletionItem => {
              // when code completing fields, expand out required variables and open braces
              const suggestedField = parentFields[
                suggestion.label
              ] as GraphQLField<void, void>;
              if (!suggestedField) {
                return suggestion;
              }

              const { definition, addedArguments } = getArgumentDefinition(
                suggestedField,
                Object.keys(currentDocsContext.variableOperationCount),
                shouldManageVariablesRef.current,
              );
              const paramsSection = definition.replace(/\$/g, '\\$');

              const isClientType =
                Array.isArray(parentType?.clientSchema?.localFields) &&
                parentType?.clientSchema?.localFields.includes(
                  suggestedField.name,
                );
              const directives = isClientType ? ' @client' : '';

              const snippet = hasFields(suggestedField.type)
                ? `${suggestion.label}${paramsSection}${directives} {\n\t$0\n}`
                : `${suggestion.label}${paramsSection}${directives}`;

              // After completing selection with fields, open autocomplete and edit variable json
              const command =
                addedArguments.length && suggestionAcceptedCommandID
                  ? {
                      id: suggestionAcceptedCommandID,
                      title: '',
                      arguments: [
                        hasFields(suggestedField.type),
                        addedArguments,
                      ],
                    }
                  : hasFields(suggestedField.type)
                    ? { id: 'editor.action.triggerSuggest', title: '' }
                    : undefined;

              return {
                label: suggestion.label,
                kind: suggestion.kind,
                detail: suggestion.detail,
                sortText: suggestion.sortText,
                documentation: suggestion.documentation,
                insertText: snippet,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                command,
                range,
                additionalTextEdits: getVariableDefinitionEdits(
                  currentDocsContext,
                  addedArguments,
                  model,
                ),
              };
            },
          ),
        };
      }

      if (state.kind === 'Directive') {
        return {
          suggestions: suggestions.map(
            (suggest): monaco.languages.CompletionItem => {
              const directive = schemaRefCurrent.getDirective(suggest.label);
              if (!directive) {
                return suggest;
              }

              const { definition, addedArguments } = getArgumentDefinition(
                directive,
                Object.keys(currentDocsContext.variableOperationCount),
                shouldManageVariablesRef.current,
              );

              const paramsSection = definition.replace(/\$/g, '\\$');

              const snippet = `${suggest.label}${paramsSection}`;

              const argsString =
                directive.args.length > 0
                  ? `(${directive.args
                      .map((a) => `${a.name}: ${a.type}`)
                      .join(', ')})`
                  : '';

              const content = [
                [
                  `\`\`\`graphql`,
                  `@${suggest.label}${argsString}`,
                  `\`\`\``,
                ].join('\n'),
              ];

              if (suggest.documentation) {
                if (typeof suggest.documentation === 'string') {
                  content.push(suggest.documentation);
                }
              }

              const command =
                addedArguments.length && suggestionAcceptedCommandID
                  ? {
                      id: suggestionAcceptedCommandID,
                      title: '',
                      arguments: [false, addedArguments],
                    }
                  : undefined;

              return {
                label: suggest.label,
                kind: suggest.kind,
                documentation: content.join('\n\n'),
                insertText: snippet,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                range,
                command,
                additionalTextEdits: getVariableDefinitionEdits(
                  currentDocsContext,
                  addedArguments,
                  model,
                ),
              };
            },
          ),
        };
      }

      if (state.kind === 'Argument' || state.kind === 'Arguments') {
        return {
          suggestions: suggestions.map(
            (suggest): monaco.languages.CompletionItem => {
              // For arguments we want to add a variable to it and perform all the variable edits
              const argumentDefinition = typeInfo.argDefs?.find(
                (argDef) => argDef.name === suggest.insertText,
              );
              if (!argumentDefinition || !typeInfo.fieldDef?.name) {
                return suggest;
              }

              if (
                state.kind === 'Argument' &&
                !getPathToOffset(model, monacoPosition)?.endState.needsAdvance
              ) {
                // argumen definition already has punction, se we won't add a variable
                return suggest;
              }
              const addedArgument = getArgument(
                typeInfo.fieldDef.name,
                argumentDefinition,
                Object.keys(currentDocsContext.variableOperationCount),
                shouldManageVariablesRef.current,
              );

              const snippet = `${addedArgument.argumentName}: ${
                addedArgument.variableName
                  ? `\\$${addedArgument.variableName}`
                  : ''
              }`;

              const command = suggestionAcceptedCommandID
                ? {
                    id: suggestionAcceptedCommandID,
                    title: '',
                    arguments: [false, [addedArgument]],
                  }
                : undefined;

              return {
                ...suggest,
                insertText: snippet,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                command,
                range,
                additionalTextEdits: getVariableDefinitionEdits(
                  currentDocsContext,
                  [addedArgument],
                  model,
                ),
              };
            },
          ),
        };
      }

      return { suggestions };
    },
  };
}
